/**
 * ED SendOut Grid Component
 *
 * Encounter-level drilldown grid for ED Send-Out (teletriage providers).
 * Displays rows from och.cqm_teletriage_ed_sendout.
 */

import { useMemo, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type SortingState,
  type ColumnDef,
} from '@tanstack/react-table'
import {
  AnimatedTableWrapper,
  AnimatedTableBody,
  AnimatedTableRow,
} from '@/components/tailwindadmin/components/animated-component/AnimatedTable'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import type { EDSendoutEncounterRow } from '@/lib/equip-types'
import { equipTable } from '@/views/equip/equip-table-styles'
import { SortableHeader } from '@/views/equip/components/SortableHeader'
import { cn } from '@/lib/utils'

interface EDSendoutGridProps {
  encounters: EDSendoutEncounterRow[]
  isLoading: boolean
}

function isEdSendoutRow(label: string): boolean {
  const lower = label.toLowerCase()
  return lower.includes('send out') || lower.includes('sendout')
}

function formatDate(val: string | null): string {
  if (!val) return '—'
  try {
    return new Date(val).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return val
  }
}

const columns: ColumnDef<EDSendoutEncounterRow>[] = [
  {
    accessorKey: 'noteId',
    header: ({ column }) => <SortableHeader label="Note ID" column={column} />,
    cell: ({ getValue }) => {
      const noteId = getValue() as number
      return (
        <a
          href={`https://eventus.afoundria.com/note/print?id=${noteId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-xs text-blue-600 dark:text-blue-400 hover:underline"
        >
          {noteId}
        </a>
      )
    },
  },
  {
    accessorKey: 'noteTreatmentDate',
    header: ({ column }) => <SortableHeader label="Encounter Date" column={column} />,
    cell: ({ getValue }) => formatDate(getValue() as string | null),
  },
  {
    accessorKey: 'facilityName',
    header: ({ column }) => <SortableHeader label="Facility" column={column} />,
    cell: ({ getValue }) => (getValue() as string | null) ?? '—',
  },
  {
    accessorKey: 'edClassificationLabel',
    header: ({ column }) => <SortableHeader label="Outcome" column={column} />,
    cell: ({ getValue }) => {
      const label = getValue() as string
      return (
        <Badge variant={isEdSendoutRow(label) ? 'lightError' : 'lightSuccess'}>
          {label}
        </Badge>
      )
    },
  },
]

export function EDSendoutGrid({ encounters, isLoading }: EDSendoutGridProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'noteTreatmentDate', desc: true },
  ])
  const [sendoutOnly, setSendoutOnly] = useState(false)

  const data = useMemo(
    () => (sendoutOnly ? encounters.filter((e) => isEdSendoutRow(e.edClassificationLabel)) : encounters),
    [encounters, sendoutOnly]
  )

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>ED Send-Out Encounters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <CardTitle>ED Send-Out Encounters</CardTitle>
          <span className="text-sm text-muted-foreground">
            {data.length} encounter{data.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground leading-none">All Outcomes</span>
          <Switch
            id="sendout-filter"
            checked={sendoutOnly}
            onCheckedChange={setSendoutOnly}
          />
          <span className="text-xs text-muted-foreground leading-none">ED Send-Outs Only</span>
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {sendoutOnly ? 'No ED send-out encounters in this quarter' : 'No encounters found for this quarter'}
          </div>
        ) : (
          <div className={equipTable.wrapper}>
            <div className="overflow-auto">
              <AnimatedTableWrapper>
                <table className={equipTable.table}>
                  <thead className={equipTable.thead}>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id} className={equipTable.theadTr}>
                        {headerGroup.headers.map((header, headerIdx) => (
                          <th key={header.id} className={cn(equipTable.th, headerIdx === 0 && equipTable.stickyTh)}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <AnimatedTableBody>
                    {table.getRowModel().rows.map((row, index) => (
                      <AnimatedTableRow
                        key={row.id}
                        index={index}
                        className={`${equipTable.tr} ${equipTable.trAlt(index)}`}
                      >
                        {row.getVisibleCells().map((cell, cellIdx) => (
                          <td key={cell.id} className={cn(equipTable.td, cellIdx === 0 && (index % 2 === 1 ? equipTable.stickyTdAlt : equipTable.stickyTd))}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </AnimatedTableRow>
                    ))}
                  </AnimatedTableBody>
                </table>
              </AnimatedTableWrapper>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
